import * as Dialog from '@radix-ui/react-dialog'
import type { ReactNode } from 'react'

import { FxIcon } from '@/components/fx'

/**
 * The 640px dialog the artboard draws: `radius-xl`, `shadow-overlay`, no
 * border, a close button in the corner.
 *
 * Radix supplies the focus trap, Escape, the scroll lock and the
 * `aria-labelledby` wiring. `components/ui/dialog` is the shadcn one —
 * bordered, `rounded-lg`, a `bg-black/80` scrim — and looks like a different
 * product beside an Fx panel; `components/fx` has no dialog shell, only
 * `FxUploadDialog`, which is one particular dialog.
 *
 * This is the second local copy of that shell (`pages/projekte/ProjektDialog`
 * is the first). It belongs in `components/fx` as `FxDialog`; that file is
 * not this wave's to touch, so the duplication is reported rather than
 * smuggled in.
 */
export function ShareDialogShell({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: ReactNode
  children?: ReactNode
  footer?: ReactNode
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/55" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 flex max-h-[90vh] w-[min(640px,calc(100vw-32px))] -translate-x-1/2 -translate-y-1/2 flex-col gap-4 overflow-y-auto rounded-xl bg-surface p-6 text-body text-text shadow-overlay sm:p-10">
          <div className="flex items-start gap-4">
            <Dialog.Title className="m-0 flex-1 text-dialog-title">{title}</Dialog.Title>
            <Dialog.Close
              className="-mr-2 -mt-2 flex h-11 w-11 flex-none items-center justify-center rounded-sm text-text"
              aria-label="Dialog schließen"
            >
              <FxIcon name="close" size={28} />
            </Dialog.Close>
          </div>
          {description && (
            <Dialog.Description className="m-0 text-body text-text-muted">
              {description}
            </Dialog.Description>
          )}
          {children}
          {footer && <div className="flex flex-wrap items-center gap-4">{footer}</div>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
